import { URDFJoint, URDFRobot } from "urdf-loader";
import { ModelRenderer } from "../ModelRenderer";
import { useEffect, useState } from "react";
import { PropValueLine } from "../common/PropValueLine";
import {
  Box,
  Card,
  Checkbox,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { Model, MotionMapperType, QRMotionMapper } from "../../models/Model";
import { MREvents } from "../MREvents";
import { QRLegJoints, QRLegs } from "../../visuals/QRAnimator";
import { ShowChart } from "@mui/icons-material";
import * as THREE from "three";
import { ObjectHighlighterButton } from "../common/ObjectHighlighterButton";

function JointEditor({ joint, model }: { joint: URDFJoint; model: Model }) {
  const [angle, setAngle] = useState(joint.angle);
  // const [leg, setLeg] = useState<QRLegs>(QRLegs.FrontLeft);
  // const [jointType, setJointType] = useState<QRLegJoints>(QRLegJoints.HipX);
  const [legAndJointType, setLegAndJointType] = useState<{
    leg: QRLegs | -1;
    jointType: QRLegJoints | -1;
  }>({
    leg: -1,
    jointType: -1,
  });

  useEffect(() => {
    const mapper = model.motionMapper as QRMotionMapper;
    const jointMap = mapper.jointMap;
    if (!jointMap) {
      console.warn("Joint map is not defined in the model motion mapper.");
      return;
    }
    console.log(jointMap);
    console.log(`Finding leg and joint type for joint: ${joint.name}`);
    for (let leg = 0; leg < jointMap.length; leg++) {
      for (let jointType = 0; jointType < jointMap[leg].length; jointType++) {
        if (jointMap[leg][jointType] === joint.name) {
          console.log(
            `Found joint ${joint.name} at leg ${leg}, jointType ${jointType}`
          );
          setLegAndJointType({
            leg: leg as QRLegs,
            jointType: jointType as QRLegJoints,
          });
          return;
        }
      }
    }
    // If not found, reset to -1
    setLegAndJointType({ leg: -1, jointType: -1 });
  }, [joint]);

  useEffect(() => {
    setAngle(joint.angle || 0);

    const mapper = model.motionMapper as QRMotionMapper;
    const jointMap = mapper.jointMap;
    if (!jointMap) {
      console.warn("Joint map is not defined in the model motion mapper.");
      return;
    }
    const { leg, jointType } = legAndJointType;
    console.log("Updating joint map:", leg, jointType, joint.name);
    // only update if leg and jointType are valid
    if (
      leg < 0 ||
      leg >= jointMap.length ||
      jointType < 0 ||
      jointType >= jointMap[leg].length
    ) {
      console.log("joint:", joint);
      console.warn("Invalid leg or joint type selected.");
      return;
    }
    jointMap[leg][jointType] = joint.name;
    console.log("Updated joint map:", jointMap);
  }, [legAndJointType]);

  return (
    <Box
      sx={{
        width: "100%",
        overflow: "auto",
        background: "#2F2F2F",
        padding: "1rem",
      }}
    >
      <PropValueLine
        labelWidth={125}
        label={"Leg"}
        value={
          <TextField
            size="small"
            select
            value={legAndJointType.leg}
            onChange={(e) => {
              const newLeg = parseInt(e.target.value) as QRLegs;
              setLegAndJointType((prev) => ({
                ...prev,
                leg: newLeg,
              }));
            }}
            sx={{ minWidth: "150px" }}
          >
            <MenuItem value={-1} key={-1} disabled>
              Select Leg
            </MenuItem>
            {Object.keys(QRLegs)
              .filter(
                (key) => isNaN(Number(key)) // Filter out numeric keys
              )
              .map((key) => {
                return (
                  <MenuItem
                    key={key}
                    value={QRLegs[key as keyof typeof QRLegs]}
                  >
                    {key}
                  </MenuItem>
                );
              })}
          </TextField>
        }
      />
      <PropValueLine
        labelWidth={125}
        label={"Joint Type"}
        value={
          <TextField
            size="small"
            select
            value={legAndJointType.jointType}
            onChange={(e) => {
              const newJointType = parseInt(e.target.value) as QRLegJoints;
              setLegAndJointType((prev) => ({
                ...prev,
                jointType: newJointType,
              }));
            }}
            sx={{ minWidth: "150px" }}
          >
            <MenuItem value={-1} key={-1} disabled>
              Select Joint
            </MenuItem>
            {Object.keys(QRLegJoints)
              .filter(
                (key) => isNaN(Number(key)) // Filter out numeric keys
              )
              .map((key) => {
                return (
                  <MenuItem
                    key={key}
                    value={QRLegJoints[key as keyof typeof QRLegJoints]}
                  >
                    {key}
                  </MenuItem>
                );
              })}
          </TextField>
        }
      />
      {(joint.jointType === "revolute" || joint.jointType === "continuous") && (
        <PropValueLine
          labelWidth={125}
          label={"Angle"}
          value={
            <>
              <TextField
                type="number"
                value={angle}
                onChange={(e) => {
                  const newAngle = parseFloat(e.target.value);
                  joint.setJointValue(newAngle);
                  setAngle(newAngle);
                }}
                variant="standard"
                size="small"
                sx={{ width: "100px" }}
              />
              <ObjectHighlighterButton object={joint} />
            </>
          }
        />
      )}
      {joint.jointType === "fixed" && (
        <PropValueLine
          labelWidth={125}
          label={"Angle"}
          value={
            <>
              <TextField
                value="fixed"
                variant="standard"
                size="small"
                sx={{ width: "100px" }}
              // disabled
              />
              <IconButton
                size="small"
                onMouseDown={() => {
                  const obj = joint;
                  if (!obj) return;
                  const axesHelper = new THREE.AxesHelper(0.1);
                  axesHelper.name = `designator`;
                  obj.add(axesHelper);
                }}
                onMouseUp={() => {
                  const obj = joint;
                  if (!obj) return;
                  const axesHelper = obj.getObjectByName(`designator`);
                  if (axesHelper) {
                    obj.remove(axesHelper);
                  }
                }}
              >
                <ShowChart />
              </IconButton>
            </>
          }
        />
      )}
    </Box>
  );
}

export function URDFQRPropsPanel({
  mr,
  robot,
}: {
  mr: ModelRenderer;
  robot: URDFRobot;
}) {
  const [invertedY, setInvertedY] = useState<boolean>(false);
  const [selectedJoint, setSelectedJoint] = useState<URDFJoint | null>(null);
  useEffect(() => {
    if (!robot) {
      return;
    }
    const model = mr.loader.model;
    // console.log({ robot });
    setSelectedJoint(null);
    setInvertedY((model.motionMapper as QRMotionMapper).invertedY);
    if (model.motionMapper.type !== MotionMapperType.QuadrupedRobot) {
      const qrMotionMapper: QRMotionMapper = {
        type: MotionMapperType.QuadrupedRobot,
        walkingHeight: 0.65,
        thighLength: 0.2, // Example value, adjust as needed
        shinLength: 0.2, // Example value, adjust as needed
        stride: 0.1,
        jointMap: [
          ["FL_HipX_joint", "FL_HipY_joint", "FL_Knee_joint", "FL_Ankle"],
          ["FR_HipX_joint", "FR_HipY_joint", "FR_Knee_joint", "FR_Ankle"],
          ["HL_HipX_joint", "HL_HipY_joint", "HL_Knee_joint", "HL_Ankle"],
          ["HR_HipX_joint", "HR_HipY_joint", "HR_Knee_joint", "HR_Ankle"],
        ],
        invertedY: true,
      };
      model.motionMapper = qrMotionMapper;
      mr.emitEvent(MREvents.ModelUpdated, model);
      mr.loader.adapt();
    }
  }, [robot]);
  if (!robot) {
    return <div>No URDF Robot loaded</div>;
  }
  const mapper = mr.loader.model.motionMapper as QRMotionMapper;
  return (
    <>
      <PropValueLine
        labelWidth={125}
        label="Robot type"
        value={<TextField variant="standard" size="small" value="Quadruped" />}
      />
      <PropValueLine
        labelWidth={125}
        label="Inverted Y"
        value={
          <Checkbox
            checked={invertedY}
            onChange={(e, checked) => {
              setInvertedY(checked);
              mapper.invertedY = checked;
            }}
          />
        }
      />
      <PropValueLine
        labelWidth={125}
        label="Joint"
        value={
          <TextField
            size="small"
            select
            value={selectedJoint ? selectedJoint.name : ""}
            onChange={(e) => {
              const jointName = e.target.value;
              const joint = robot.joints[jointName];
              if (joint) {
                setSelectedJoint(joint);
              }
            }}
            sx={{ minWidth: "150px" }}
          >
            <MenuItem value="" key="none" disabled>
              Select Joint
            </MenuItem>
            {Object.keys(robot.joints)
              .map((key) => robot.joints[key])
              // .filter((joint) => joint.angle !== undefined)
              .map((joint) => (
                <MenuItem key={joint.name} value={joint.name}>
                  {joint.name}
                </MenuItem>
              ))}
          </TextField>
        }
      />
      {selectedJoint && (
        <JointEditor joint={selectedJoint} model={mr.loader.model} />
      )}
    </>
  );
}
